/**
 * RouteLegend - Legenda de cores dos segmentos de rota
 * Exibe lista de segmentos com suas cores correspondentes
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { LEG_COLORS } from '../utils/colorUtils';

const RouteLegend = ({
  routes = [],
  vehicles = [],
  selectedRouteIndex = null,
  compact = false,
}) => {
  const tokens = usePremiumTokens();
  const [expanded, setExpanded] = useState(true);

  // Construir dados da legenda
  const legendData = useMemo(() => {
    if (routes.length === 0) return [];

    return routes.map((route, routeIndex) => {
      const vehicle = vehicles.find((v) => v.id === route.vehicleId);
      const steps = route.steps?.filter((s) => s.type === 'job') || [];

      // Gerar legs com cores
      const legs = [];
      const legCount = steps.length;

      // Adicionar leg inicial: Veiculo -> Primeira parada
      if (legCount > 0) {
        legs.push({
          id: `${routeIndex}-start`,
          from: vehicle?.name || 'Inicio',
          to: steps[0].name || `Parada 1`,
          color: LEG_COLORS[0 % LEG_COLORS.length],
          isStart: true,
        });
      }

      // Adicionar legs intermediarios
      for (let i = 0; i < legCount - 1; i += 1) {
        legs.push({
          id: `${routeIndex}-${i}`,
          from: steps[i].name || `Parada ${i + 1}`,
          to: steps[i + 1].name || `Parada ${i + 2}`,
          color: LEG_COLORS[(i + 1) % LEG_COLORS.length],
        });
      }

      return {
        routeIndex,
        vehicleName: vehicle?.name || `Veiculo ${routeIndex + 1}`,
        vehicleColor: route.routeColor || tokens?.accent?.primary,
        legs,
        totalDistance: route.distance,
        totalDuration: route.duration,
      };
    });
  }, [routes, vehicles, tokens]);

  // Se nao tem rotas, nao renderizar
  if (legendData.length === 0) return null;

  // Versao compacta - so mostrar resumo
  if (compact) {
    return (
      <Box
        sx={{
          backgroundColor: tokens?.surface?.glass,
          backdropFilter: 'blur(12px)',
          borderRadius: tokens?.radius?.md,
          border: `1px solid ${tokens?.border?.subtle}`,
          p: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon sx={{ color: tokens?.accent?.primary, fontSize: 18 }} />
          <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px', fontWeight: 500 }}>
            {routes.length} rota(s) - {legendData.reduce((sum, r) => sum + r.legs.length, 0)} segmentos
          </Typography>
        </Box>
      </Box>
    );
  }

  // Versao completa com lista de legs
  return (
    <Box
      sx={{
        backgroundColor: tokens?.surface?.glass,
        backdropFilter: 'blur(12px)',
        borderRadius: tokens?.radius?.lg,
        border: `1px solid ${tokens?.border?.subtle}`,
        overflow: 'hidden',
        minWidth: 220,
        maxWidth: 280,
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${tokens?.border?.subtle}` : 'none',
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}08`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon sx={{ color: tokens?.accent?.primary, fontSize: 18 }} />
          <Typography sx={{ color: tokens?.text?.primary, fontSize: '12px', fontWeight: 600 }}>
            SEGMENTOS DA ROTA
          </Typography>
        </Box>
        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? (
            <CollapseIcon sx={{ fontSize: 18, color: tokens?.text?.tertiary }} />
          ) : (
            <ExpandIcon sx={{ fontSize: 18, color: tokens?.text?.tertiary }} />
          )}
        </IconButton>
      </Box>

      {/* Lista de rotas/legs */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5, pt: 1, maxHeight: 300, overflow: 'auto' }}>
          {legendData.map((routeData, idx) => (
            <Box
              key={routeData.routeIndex}
              sx={{
                mb: idx < legendData.length - 1 ? 2 : 0,
              }}
            >
              {/* Nome do veiculo (se mais de uma rota) */}
              {legendData.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: routeData.vehicleColor,
                    }}
                  />
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '11px', fontWeight: 600 }}>
                    {routeData.vehicleName}
                  </Typography>
                </Box>
              )}

              {/* Lista de legs */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {routeData.legs.map((leg, legIdx) => (
                  <Box
                    key={leg.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: legendData.length > 1 ? 1.5 : 0,
                      borderRadius: tokens?.radius?.sm,
                      backgroundColor: selectedRouteIndex === routeData.routeIndex
                        ? `${leg.color}15`
                        : 'transparent',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {/* Cor do leg */}
                    <Box
                      sx={{
                        width: 16,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: leg.color,
                        flexShrink: 0,
                      }}
                    />

                    {/* Descricao do leg */}
                    <Typography
                      sx={{
                        color: tokens?.text?.primary,
                        fontSize: '11px',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {leg.isStart && (
                        <Box component="span" sx={{ color: tokens?.text?.tertiary }}>
                          Inicio{' '}
                        </Box>
                      )}
                      {leg.from}
                      <Box component="span" sx={{ color: tokens?.text?.tertiary, mx: 0.5 }}>
                        {' -> '}
                      </Box>
                      {leg.to}
                    </Typography>

                    {/* Numero do leg */}
                    <Typography
                      sx={{
                        color: tokens?.text?.tertiary,
                        fontSize: '10px',
                        fontWeight: 500,
                        minWidth: 16,
                        textAlign: 'right',
                      }}
                    >
                      {legIdx + 1}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RouteLegend;
